import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { XMarkIcon, MagnifyingGlassIcon, CheckCircleIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { colaboradoresService, type Colaborador, type CreateColaboradorDto } from '../services/colaboradores.service';
import { examesService, type ExameItem, type ExameColaboradorItem } from '../services/exames.service';
import { formatCPF } from '../utils/formatters';
import { unmask } from '../utils/masks';
import FormattedInput from '../components/FormattedInput';
import FormField from '../components/FormField';

interface NovoExameModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = 'colaborador' | 'exames' | 'resumo' | 'finalizado';

export default function NovoExame({ isOpen, onClose }: NovoExameModalProps) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('colaborador');
  const [selectedColaboradorId, setSelectedColaboradorId] = useState<number | null>(null);
  const [selectedExames, setSelectedExames] = useState<number[]>([]);
  const [searchColaborador, setSearchColaborador] = useState('');
  const [searchExame, setSearchExame] = useState('');
  const [criarNovoColaborador, setCriarNovoColaborador] = useState(false);

  // Formulário de novo colaborador
  const [formData, setFormData] = useState<CreateColaboradorDto>({
    nome: '',
    cpf: '',
    rg: '',
    email: '',
    telefone: '',
    data_nascimento: '',
    data_admissao: '',
    data_demissao: '',
    sexo: '',
    formacao_academica_id: undefined,
    cargo_id: undefined,
    setor_id: undefined,
    cargo_nome: undefined,
    setor_nome: undefined,
  });

  // Buscar colaboradores
  const { data: colaboradoresResponse } = useQuery({
    queryKey: ['colaboradores', { limit: 1000 }],
    queryFn: () => colaboradoresService.findAll({ page: 1, limit: 1000 }),
    enabled: isOpen,
  });

  // Buscar exames disponíveis
  const { data: examesDisponiveis = [] } = useQuery({
    queryKey: ['exames-disponiveis'],
    queryFn: () => examesService.getExamesDisponiveis(),
    enabled: isOpen,
  });

  // Buscar exames já vinculados ao colaborador selecionado
  const { data: examesVinculados = [] } = useQuery({
    queryKey: ['exames-colaborador', selectedColaboradorId],
    queryFn: () => examesService.getExamesPorColaborador(selectedColaboradorId!),
    enabled: isOpen && selectedColaboradorId !== null && step !== 'colaborador',
  });



  // Mutation para criar colaborador
  const createColaboradorMutation = useMutation({
    mutationFn: (data: CreateColaboradorDto) => colaboradoresService.create(data),
    onSuccess: (newColaborador) => {
      queryClient.invalidateQueries({ queryKey: ['colaboradores'] });
      setSelectedColaboradorId(newColaborador.id);
      setCriarNovoColaborador(false);
      setStep('exames');
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      console.error('❌ Erro ao criar colaborador:', error);
      alert(error?.response?.data?.message || error?.message || 'Erro ao criar colaborador');
    },
  });

  // Mutation para vincular exames
  const vincularMutation = useMutation({
    mutationFn: async ({ colaboradorId, exameId }: { colaboradorId: number; exameId: number }) => {
      return examesService.vincularExameColaborador(colaboradorId, exameId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exames-colaborador'] });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      console.error('❌ Erro ao vincular exame:', error);
      alert(error?.response?.data?.message || error?.message || 'Erro ao vincular exame');
    },
  });

  // Filtrar colaboradores
  const colaboradores = colaboradoresResponse?.data || [];
  const colaboradoresFiltrados = colaboradores.filter((col: Colaborador) => {
    if (!searchColaborador) return true;
    const search = searchColaborador.toLowerCase();
    return (
      col.nome.toLowerCase().includes(search) ||
      col.cpf?.toLowerCase().includes(search) ||
      col.email?.toLowerCase().includes(search)
    );
  });

  // Filtrar exames
  const examesFiltrados = examesDisponiveis.filter((exame: ExameItem) => {
    if (!searchExame) return true;
    const search = searchExame.toLowerCase();
    return (
      exame.nome.toLowerCase().includes(search) ||
      exame.descricao?.toLowerCase().includes(search)
    );
  });

  // IDs dos exames já vinculados
  const examesVinculadosIds = examesVinculados.map((e: ExameColaboradorItem) => e.exame_id || e.id);

  // Exames selecionados com detalhes
  const examesSelecionadosDetalhes = examesDisponiveis.filter((exame: ExameItem) => selectedExames.includes(exame.id));

  const handleClose = () => {
    setStep('colaborador');
    setSelectedColaboradorId(null);
    setSelectedExames([]);
    setSearchColaborador('');
    setSearchExame('');
    setCriarNovoColaborador(false);
    setFormData({
      nome: '',
      cpf: '',
      rg: '',
      email: '',
      telefone: '',
      data_nascimento: '',
      data_admissao: '',
      data_demissao: '',
      sexo: '',
      formacao_academica_id: undefined,
      cargo_id: undefined,
      setor_id: undefined,
      cargo_nome: undefined,
      setor_nome: undefined,
    });
    onClose();
  };

  const handleToggleExame = (exameId: number) => {
    setSelectedExames((prev) => {
      if (prev.includes(exameId)) {
        return prev.filter((id) => id !== exameId);
      } else {
        return [...prev, exameId];
      }
    });
  };

  const handleCriarColaborador = () => {
    if (!formData.nome || !formData.cpf) {
      alert('Preencha nome e CPF');
      return;
    }

    if (!formData.data_admissao) {
      alert('Data de admissão é estritamente obrigatória');
      return;
    }

    const submitData: CreateColaboradorDto = {
      ...formData,
      nome: formData.nome,
      cpf: unmask(formData.cpf),
      telefone: formData.telefone ? unmask(formData.telefone) : undefined,
    };

    delete submitData.empresa_id;

    if (submitData.cargo_nome && !submitData.cargo_id) {
      // Manter cargo_nome
    } else {
      delete submitData.cargo_nome;
    }

    if (submitData.setor_nome && !submitData.setor_id) {
      // Manter setor_nome
    } else {
      delete submitData.setor_nome;
    }

    createColaboradorMutation.mutate(submitData);
  };

  const handleVincularExames = async () => {
    if (!selectedColaboradorId) {
      alert('Selecione um colaborador');
      return;
    }

    if (selectedExames.length === 0) {
      alert('Selecione pelo menos um exame');
      return;
    }

    // Vincular todos os exames selecionados
    await Promise.all(
      selectedExames.map((exameId) =>
        vincularMutation.mutateAsync({
          colaboradorId: selectedColaboradorId,
          exameId,
        })
      )
    );

    setStep('resumo');
  };

  const handleFinalizar = () => {
    setStep('finalizado');
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  const colaboradorSelecionado = colaboradores.find((c: Colaborador) => c.id === selectedColaboradorId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start md:items-center justify-center z-50 p-0 md:p-4 overflow-hidden">
      <div className="bg-white rounded-none md:rounded-lg p-4 md:p-6 w-full md:w-full md:max-w-4xl h-full md:h-auto md:max-h-[90vh] md:my-auto flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-900">Novo Exame</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Fechar"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Steps Indicator */}
        <div className="flex items-center justify-center mb-6 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <div className={`flex items-center ${step === 'colaborador' ? 'text-green-600' : step === 'exames' || step === 'resumo' || step === 'finalizado' ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'colaborador' ? 'bg-green-600 text-white' : step === 'exames' || step === 'resumo' || step === 'finalizado' ? 'bg-green-100 text-green-600' : 'bg-gray-200'}`}>
                {step === 'exames' || step === 'resumo' || step === 'finalizado' ? '✓' : '1'}
              </div>
              <span className="ml-2 text-sm font-medium">Colaborador</span>
            </div>
            <div className="w-12 h-0.5 bg-gray-300"></div>
            <div className={`flex items-center ${step === 'exames' ? 'text-green-600' : step === 'resumo' || step === 'finalizado' ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'exames' ? 'bg-green-600 text-white' : step === 'resumo' || step === 'finalizado' ? 'bg-green-100 text-green-600' : 'bg-gray-200'}`}>
                {step === 'resumo' || step === 'finalizado' ? '✓' : '2'}
              </div>
              <span className="ml-2 text-sm font-medium">Exames</span>
            </div>
            <div className="w-12 h-0.5 bg-gray-300"></div>
            <div className={`flex items-center ${step === 'resumo' ? 'text-green-600' : step === 'finalizado' ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'resumo' ? 'bg-green-600 text-white' : step === 'finalizado' ? 'bg-green-100 text-green-600' : 'bg-gray-200'}`}>
                {step === 'finalizado' ? '✓' : '3'}
              </div>
              <span className="ml-2 text-sm font-medium">Resumo</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Step 1: Colaborador */}
          {step === 'colaborador' && (
            <div className="space-y-6">
              <div className="flex gap-4 mb-4">
                <button
                  onClick={() => setCriarNovoColaborador(false)}
                  className={`flex-1 px-4 py-2 rounded-lg transition-colors ${!criarNovoColaborador
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  Selecionar Existente
                </button>
                <button
                  onClick={() => setCriarNovoColaborador(true)}
                  className={`flex-1 px-4 py-2 rounded-lg transition-colors ${criarNovoColaborador
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  <UserPlusIcon className="w-5 h-5 inline mr-2" />
                  Novo Colaborador
                </button>
              </div>

              {!criarNovoColaborador ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Buscar Colaborador
                  </label>
                  <div className="relative mb-4">
                    <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchColaborador}
                      onChange={(e) => setSearchColaborador(e.target.value)}
                      placeholder="Buscar por nome, CPF ou email..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
                    {colaboradoresFiltrados.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        Nenhum colaborador encontrado
                      </div>
                    ) : (
                      colaboradoresFiltrados.map((colaborador: Colaborador) => (
                        <button
                          key={colaborador.id}
                          onClick={() => {
                            setSelectedColaboradorId(colaborador.id);
                            setStep('exames');
                          }}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${selectedColaboradorId === colaborador.id
                              ? 'bg-green-50 border-green-200'
                              : ''
                            }`}
                        >
                          <div className="font-medium text-gray-900">{colaborador.nome}</div>
                          <div className="text-sm text-gray-500">
                            {colaborador.cpf && formatCPF(colaborador.cpf)} • {colaborador.email || 'Sem email'}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label="Nome" required={true}>
                      <input
                        type="text"
                        required
                        value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="Nome completo"
                      />
                    </FormField>

                    <FormField label="CPF" required={true}>
                      <FormattedInput
                        type="cpf"
                        required
                        value={formData.cpf}
                        onChange={(val) => setFormData({ ...formData, cpf: val })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="000.000.000-00"
                      />
                    </FormField>

                    <FormField label="Email">
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="email@exemplo.com"
                      />
                    </FormField>

                    <FormField label="Telefone">
                      <FormattedInput
                        type="phone"
                        value={formData.telefone}
                        onChange={(val) => setFormData({ ...formData, telefone: val })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="(00) 00000-0000"
                      />
                    </FormField>

                    <FormField label="Data de Admissão" required={true}>
                      <input
                        type="date"
                        required
                        value={formData.data_admissao}
                        onChange={(e) => setFormData({ ...formData, data_admissao: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </FormField>

                    <FormField label="Sexo">
                      <select
                        value={formData.sexo}
                        onChange={(e) => setFormData({ ...formData, sexo: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      >
                        <option value="">Selecione</option>
                        <option value="M">Masculino</option>
                        <option value="F">Feminino</option>
                      </select>
                    </FormField>
                  </div>
                  <button
                    onClick={handleCriarColaborador}
                    disabled={createColaboradorMutation.isPending}
                    className="w-full px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    {createColaboradorMutation.isPending ? 'Criando...' : 'Criar e Continuar'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Exames */}
          {step === 'exames' && (
            <div className="space-y-4">
              {colaboradorSelecionado && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <div className="font-medium text-green-900">Colaborador Selecionado:</div>
                  <div className="text-sm text-green-700">
                    {colaboradorSelecionado.nome} {colaboradorSelecionado.cpf && `• ${formatCPF(colaboradorSelecionado.cpf)}`}
                  </div>
                </div>
              )}
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buscar Exames
              </label>
              <div className="relative mb-4">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchExame}
                  onChange={(e) => setSearchExame(e.target.value)}
                  placeholder="Buscar exame..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div className="border border-gray-300 rounded-lg max-h-96 overflow-y-auto">
                {examesFiltrados.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    Nenhum exame encontrado
                  </div>
                ) : (
                  examesFiltrados.map((exame: ExameItem) => {
                    const jaVinculado = examesVinculadosIds.includes(exame.id);
                    const selecionado = selectedExames.includes(exame.id);
                    return (
                      <div
                        key={exame.id}
                        className={`px-4 py-3 border-b border-gray-100 last:border-b-0 ${jaVinculado ? 'bg-gray-50 opacity-60' : ''
                          }`}
                      >
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selecionado}
                            onChange={() => handleToggleExame(exame.id)}
                            disabled={jaVinculado}
                            className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{exame.nome}</div>
                            {exame.descricao && (
                              <div className="text-sm text-gray-500">{exame.descricao}</div>
                            )}
                          </div>
                          {jaVinculado && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                              Já vinculado
                            </span>
                          )}
                        </label>
                      </div>
                    );
                  })
                )}
              </div>
              {selectedExames.length > 0 && (
                <div className="mt-2 text-sm text-gray-600">
                  {selectedExames.length} exame(s) selecionado(s)
                </div>
              )}
            </div>
          )}

          {/* Step 3: Resumo */}
          {step === 'resumo' && (
            <div className="space-y-4">
              {colaboradorSelecionado && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-2">Colaborador</h3>
                  <div className="text-sm text-gray-600">
                    <div>{colaboradorSelecionado.nome}</div>
                    {colaboradorSelecionado.cpf && <div>CPF: {formatCPF(colaboradorSelecionado.cpf)}</div>}
                    {colaboradorSelecionado.email && <div>Email: {colaboradorSelecionado.email}</div>}
                  </div>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-3">Exames Selecionados</h3>
                <div className="space-y-2">
                  {examesSelecionadosDetalhes.map((exame: ExameItem) => (
                    <div key={exame.id} className="flex justify-between items-center text-sm">
                      <span>{exame.nome}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Finalizado */}
          {step === 'finalizado' && (
            <div className="text-center py-8">
              <CheckCircleIcon className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Exames vinculados com sucesso!</h3>
              <p className="text-gray-600">O processo foi concluído.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        {step !== 'finalizado' && (
          <div className="flex gap-3 pt-4 border-t border-gray-200 flex-shrink-0">
            {step !== 'colaborador' && (
              <button
                type="button"
                onClick={() => {
                  if (step === 'exames') {
                    setStep('colaborador');
                    setSelectedExames([]);
                  } else if (step === 'resumo') {
                    setStep('exames');
                  }
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Voltar
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            {step === 'exames' && (
              <button
                type="button"
                onClick={handleVincularExames}
                disabled={selectedExames.length === 0 || vincularMutation.isPending}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {vincularMutation.isPending ? 'Vinculando...' : 'Continuar para Resumo'}
              </button>
            )}
            {step === 'resumo' && (
              <button
                type="button"
                onClick={handleFinalizar}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors"
              >
                Finalizar
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
